import { useState, useRef, useCallback } from 'react';
import { Shape } from '../types/Shape';

export interface UseCanvasInteractionsReturn {
  // Drawing state
  isDrawing: boolean;
  setIsDrawing: React.Dispatch<React.SetStateAction<boolean>>;
  currentShape: Partial<Shape> | null;
  setCurrentShape: React.Dispatch<React.SetStateAction<Partial<Shape> | null>>;

  // Resize state
  isResizing: boolean;
  setIsResizing: React.Dispatch<React.SetStateAction<boolean>>;
  resizeHandle: string | null;
  setResizeHandle: React.Dispatch<React.SetStateAction<string | null>>;
  resizeStartPos: { x: number; y: number } | null;
  setResizeStartPos: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  resizeStartShape: { x: number; y: number; width: number; height: number } | null;
  setResizeStartShape: React.Dispatch<React.SetStateAction<{ x: number; y: number; width: number; height: number } | null>>;

  // Snap guides state
  alignmentGuides: Array<{ type: 'vertical' | 'horizontal'; position: number }>;
  setAlignmentGuides: React.Dispatch<React.SetStateAction<Array<{ type: 'vertical' | 'horizontal'; position: number }>>>;

  // Snap calculation refs
  snapCalculationRafRef: React.MutableRefObject<number | null>;
  pendingSnapCalculationRef: React.MutableRefObject<{ shapeId: string; newX: number; newY: number } | null>;
  lastSnappedPositionRef: React.MutableRefObject<{ x: number | null; y: number | null } | null>;

  // PDF viewer state
  showPDFViewer: boolean;
  setShowPDFViewer: React.Dispatch<React.SetStateAction<boolean>>;

  // Toolbar state
  toolbarWidth: number;
  setToolbarWidth: React.Dispatch<React.SetStateAction<number>>;

  // Utility functions
  clearDrawingState: () => void;
  clearResizeState: () => void;
  clearSnapState: () => void;
}

export function useCanvasInteractions(): UseCanvasInteractionsReturn {
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentShape, setCurrentShape] = useState<Partial<Shape> | null>(null);

  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [resizeStartPos, setResizeStartPos] = useState<{ x: number; y: number } | null>(null);
  const [resizeStartShape, setResizeStartShape] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  // Snap guides state
  const [alignmentGuides, setAlignmentGuides] = useState<Array<{ type: 'vertical' | 'horizontal'; position: number }>>([]);

  // Snap calculation refs
  const snapCalculationRafRef = useRef<number | null>(null);
  const pendingSnapCalculationRef = useRef<{ shapeId: string; newX: number; newY: number } | null>(null);
  const lastSnappedPositionRef = useRef<{ x: number | null; y: number | null } | null>(null);

  // PDF viewer state
  const [showPDFViewer, setShowPDFViewer] = useState(false);

  // Toolbar state
  const [toolbarWidth, setToolbarWidth] = useState(0);

  // Clear drawing state
  const clearDrawingState = useCallback(() => {
    setIsDrawing(false);
    setCurrentShape(null);
  }, []);

  // Clear resize state
  const clearResizeState = useCallback(() => {
    setIsResizing(false);
    setResizeHandle(null);
    setResizeStartPos(null);
    setResizeStartShape(null);
  }, []);

  // Clear snap state
  const clearSnapState = useCallback(() => {
    setAlignmentGuides([]);
    if (snapCalculationRafRef.current !== null) {
      cancelAnimationFrame(snapCalculationRafRef.current);
      snapCalculationRafRef.current = null;
    }
    pendingSnapCalculationRef.current = null;
    lastSnappedPositionRef.current = null;
  }, []);

  return {
    // Drawing state
    isDrawing,
    setIsDrawing,
    currentShape,
    setCurrentShape,

    // Resize state
    isResizing,
    setIsResizing,
    resizeHandle,
    setResizeHandle,
    resizeStartPos,
    setResizeStartPos,
    resizeStartShape,
    setResizeStartShape,

    // Snap guides state
    alignmentGuides,
    setAlignmentGuides,

    // Snap calculation refs
    snapCalculationRafRef,
    pendingSnapCalculationRef,
    lastSnappedPositionRef,

    // PDF viewer state
    showPDFViewer,
    setShowPDFViewer,

    // Toolbar state
    toolbarWidth,
    setToolbarWidth,

    // Utility functions
    clearDrawingState,
    clearResizeState,
    clearSnapState,
  };
}
